import { useRef, useEffect } from 'react'

interface Particle {
  x: number
  y: number
  size: number
  life: number
  velX: number
  velY: number
}

export default function SmokeTrail() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const particles: Particle[] = []

  useEffect(() => {
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    let animationId: number

    // Resize canvas to cover full viewport
    function resize() {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // Spawn a particle on each mouse move
    function onMouseMove(e: MouseEvent) {
      particles.push({
        x: e.clientX,
        y: e.clientY,
        size: 10 + Math.random() * 10,
        life: 60,
        velX: (Math.random() - 0.5) * 1.5,
        velY: -(Math.random() * 1.5)
      })
    }
    window.addEventListener('mousemove', onMouseMove)

    // Animation loop
    function draw() {
      // Clear canvas completely each frame
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw and update particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        // Update position and life
        p.x += p.velX
        p.y += p.velY
        p.life--

        // Calculate alpha based on remaining life
        const alpha = p.life / 60
        if (alpha <= 0) {
          particles.splice(i, 1)
          continue
        }

        // Create radial gradient for smoke effect
        const grad = ctx.createRadialGradient(
          p.x, p.y, 0,
          p.x, p.y, p.size
        )
        grad.addColorStop(0, `rgba(200, 200, 200, ${alpha * 0.3})`)
        grad.addColorStop(1, 'rgba(200, 200, 200, 0)')

        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fill()
      }

      animationId = requestAnimationFrame(draw)
    }

    draw()

    // Cleanup on unmount
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(animationId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 9999
      }}
    />
  )
}
